import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import useAdminEssays from "@/pages/admin/essays/useAdminEssays";
import { clearCache } from "@/pages/admin/adminCache";

const KEY_STORAGE = "vbb_admin_key";

/**
 * /admin/essays — Working folder for essays. Everything is a draft
 * until explicitly published; drafts and published items both live
 * here, distinguished by badge.
 */
export default function AdminEssays() {
  const navigate = useNavigate();
  const { key, essays, status, deleteEssay } = useAdminEssays();
  const [filter, setFilter] = useState("all"); // all | draft | published

  useEffect(() => {
    if (!key || status.state === "unauthorized") {
      navigate("/login?next=/admin/essays", { replace: true });
    }
  }, [key, status.state, navigate]);

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    navigate("/login?next=/admin/essays", { replace: true });
  };

  const filtered = essays.filter((e) => {
    if (filter === "draft") return !e.published;
    if (filter === "published") return e.published;
    return true;
  });

  if (!key) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-essays-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            / Essays
          </div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-essays-title"
          >
            <BoxedTitle size="lg" thick>
              Essays.
            </BoxedTitle>
          </h1>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[#6b6b6b]">
            Working folder — drafts and published pieces. Not linked from
            the public site until you promote something.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-back-to-home"
          >
            ← Admin
          </Link>
          <Link
            to="/admin/references"
            className="vbb-btn vbb-btn-ghost"
            data-testid="essays-open-references"
          >
            References
          </Link>
          <Link
            to="/admin/essays/new"
            className="vbb-btn"
            data-testid="essays-new-btn"
          >
            New essay
          </Link>
          <button
            onClick={onLock}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      <div
        className="mt-8 flex flex-wrap gap-2"
        data-testid="essay-filters"
      >
        {[
          { id: "all", label: `All (${essays.length})` },
          {
            id: "draft",
            label: `Drafts (${essays.filter((e) => !e.published).length})`,
          },
          {
            id: "published",
            label: `Published (${
              essays.filter((e) => e.published).length
            })`,
          },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`essay-filter-${f.id}`}
            className={`px-4 py-2 font-mono-tiny uppercase tracking-wider border ${
              filter === f.id
                ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#fafafa]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {status.state === "error" && (
        <div
          className="mt-8 p-4 border border-[#e34234] text-[#e34234] font-mono-tiny"
          data-testid="admin-error-banner"
        >
          {status.error}
        </div>
      )}

      <div className="mt-8" data-testid="essay-list">
        {status.state === "ready" && filtered.length === 0 && (
          <div
            className="p-10 text-center font-mono-tiny text-[#6b6b6b] border border-[#0a0a0a]"
            data-testid="essay-empty-state"
          >
            {essays.length === 0
              ? "No essays yet. Start a new one — everything's a draft by default."
              : "No essays match this filter."}
          </div>
        )}
        {filtered.map((essay) => (
          <article
            key={essay.id}
            className="border border-[#0a0a0a] p-6 bg-white mb-4"
            data-testid={`essay-row-${essay.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="font-display uppercase text-2xl tracking-tight">
                    <Link
                      to={`/admin/essays/${essay.id}`}
                      className="hover:text-[#e34234] transition-colors"
                      data-testid={`essay-edit-link-${essay.id}`}
                    >
                      {essay.title}
                    </Link>
                  </h2>
                  <span
                    className={`px-2 py-0.5 font-mono-tiny uppercase tracking-wider ${
                      essay.published
                        ? "bg-[#e34234] text-white"
                        : "bg-white text-[#0a0a0a] border border-[#0a0a0a]"
                    }`}
                    data-testid={`essay-badge-${essay.id}`}
                  >
                    {essay.published ? "Published" : "Draft"}
                  </span>
                </div>
                <div className="font-mono-tiny text-[#6b6b6b] mt-1">
                  {essay.slug} · {essay.reference_ids?.length || 0} refs
                </div>
                {essay.excerpt && (
                  <p className="mt-3 text-[15px] leading-relaxed text-[#2a2a2a]">
                    {essay.excerpt}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={`/admin/essays/${essay.id}`}
                  className="vbb-btn vbb-btn-ghost"
                  data-testid={`essay-open-btn-${essay.id}`}
                >
                  Open
                </Link>
                <button
                  type="button"
                  onClick={async () => {
                    if (
                      window.confirm(
                        `Delete "${essay.title}"? This cannot be undone.`
                      )
                    ) {
                      await deleteEssay(essay.id);
                    }
                  }}
                  className="vbb-btn vbb-btn-ghost text-[#e34234] border-[#e34234]"
                  data-testid={`essay-delete-btn-${essay.id}`}
                >
                  Delete
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}
